import { requestBlob, requestJson } from "services/http";

const base = "/api/padihat/rtct/";
const device = (mac, resource) =>
  `${base}devices/${encodeURIComponent(mac)}/${resource}/`;
const CAPTURE_MODES = ["stream", "burst", "all"];
const captureMode = (mode) => (CAPTURE_MODES.includes(mode) ? mode : "stream");
// ?kind= on the bursts and analysis endpoints (firmware .58): rich readings,
// the other bursts, or both (the server's default when left out).
const BURST_KINDS = ["rich", "burst", "all"];
const kindQuery = (kind) => (BURST_KINDS.includes(kind) ? `&kind=${kind}` : "");
const json = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});
export const rtctApi = {
  devices: (signal) => requestJson(`${base}devices/`, { signal }),
  // Battery runway (read-only): one node's days left at its schedule, its
  // daily harvest against use and its active battery alerts; `fleetPower`
  // lists every node with readings in the last 8 days, most urgent first.
  power: (mac, signal) => requestJson(device(mac, "power"), { signal }),
  fleetPower: (signal) => requestJson(`${base}power/`, { signal }),
  // Satellite passes (Sentinel-2, Landsat) over the node for the next `days`
  // (1-14), and the node's schedule with a ground-truth packet at each:
  // `dry_run` previews it, otherwise it is sent as a settings request.
  overpasses: (mac, signal, days = 14) =>
    requestJson(
      `${device(mac, "overpasses")}?days=${Math.max(1, Math.min(14, Math.round(days) || 14))}`,
      { signal },
    ),
  schedulePasses: (mac, body) =>
    requestJson(device(mac, "overpasses/schedule"), json("POST", body)),
  telemetry: (mac, signal) =>
    requestJson(`${device(mac, "telemetry")}?limit=500`, { signal }),
  frames: (mac, signal, cursor) =>
    requestJson(
      `${device(mac, "frames")}?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      { signal },
    ),
  calibration: (mac, signal) =>
    requestJson(device(mac, "calibration"), { signal }),
  saveCalibration: (mac, body) =>
    requestJson(device(mac, "calibration"), json("PUT", body)),
  settings: (mac, signal) => requestJson(device(mac, "settings"), { signal }),
  applySettings: (mac, settings) =>
    requestJson(device(mac, "settings"), json("POST", { settings })),
  live: (mac, signal, since) =>
    requestJson(
      `${device(mac, "live")}${since ? `?since=${encodeURIComponent(since)}` : ""}`,
      { signal },
    ),
  setLive: (mac, body) => requestJson(device(mac, "live"), json("POST", body)),
  // Radar diagnostics: the sensor's read-back settings, the latest reading's
  // diagnostics and the stored echo-profile captures. Radar commands
  // (radar_blind_mm, radar_raw, radar_peaks) go through applySettings.
  radar: (mac, signal) => requestJson(device(mac, "radar"), { signal }),
  radarCapture: (id, signal) =>
    requestJson(`${base}radar-captures/${encodeURIComponent(id)}/`, { signal }),
  // Radar live: captures the node streamed in live mode after `since`, oldest
  // first (at most 300 per page), and the waterfall of the last `minutes`
  // (at most 720 minutes and 720 columns). Streaming is set through
  // applySettings (radar_stream, radar_stream_s, and the experimental
  // radar_raw_mode from firmware .55). Burst captures (firmware .56) are
  // stored as captures too: `mode` picks stream, burst or all, and Radar live
  // asks for the stream only. Servers before bursts ignore it.
  radarStream: (mac, signal, { since, limit = 300, mode = "stream" } = {}) =>
    requestJson(
      `${device(mac, "radar/stream")}?${since ? `since=${encodeURIComponent(since)}&` : ""}limit=${Math.max(1, Math.min(300, Math.round(limit) || 300))}&mode=${captureMode(mode)}`,
      { signal },
    ),
  radarWaterfall: (
    mac,
    signal,
    { minutes = 60, maxCols = 720, mode = "stream" } = {},
  ) =>
    requestJson(
      `${device(mac, "radar/waterfall")}?minutes=${Math.max(1, Math.min(720, Math.round(minutes) || 60))}&max_cols=${Math.max(1, Math.min(720, Math.round(maxCols) || 720))}&mode=${captureMode(mode)}`,
      { signal },
    ),
  // Rich wakes (firmware .56): the bursts taken after `since`, newest first,
  // at most 50 per page, each with its captures' spectra and the wake's
  // reading. Bursts are set through applySettings (radar_burst,
  // radar_burst_n, radar_burst_gap_s, radar_burst_every). Rich readings
  // (firmware .58: radar_rich, radar_rich_up, radar_rich_min_bv) come here
  // too, one small burst a wake with "kind": "rich"; `kind` (rich, burst or
  // all) keeps one kind.
  radarBursts: (mac, signal, { since, limit = 50, kind = null } = {}) =>
    requestJson(
      `${device(mac, "radar/bursts")}?${since ? `since=${encodeURIComponent(since)}&` : ""}limit=${Math.max(1, Math.min(50, Math.round(limit) || 50))}${kindQuery(kind)}`,
      { signal },
    ),
  // Radar analysis: our estimate beside the radar's reading, one point per
  // analysed burst over the last `days` (1-30), and the node's empty-pipe
  // profile. With `maxPoints` (10-10,000) the server keeps at most that many
  // of the points - the lowest and highest of each spell of time, the bursts'
  // own: rich readings bring one a wake; `kind` keeps one kind. The profile
  // is read and learned (from the bursts between dry_from and dry_to) at
  // radar/profile/.
  radarAnalysis: (
    mac,
    signal,
    { days = 1, maxPoints = null, kind = null } = {},
  ) =>
    requestJson(
      `${device(mac, "radar/analysis")}?days=${Math.max(1, Math.min(30, Math.round(days) || 1))}${
        Number.isFinite(maxPoints) && maxPoints > 0
          ? `&max_points=${Math.max(10, Math.min(10000, Math.round(maxPoints)))}`
          : ""
      }${kindQuery(kind)}`,
      { signal },
    ),
  radarProfile: (mac, signal) =>
    requestJson(device(mac, "radar/profile"), { signal }),
  // Rain at the node from its own readings over the last `days` (1-31; the
  // server's default is 7): its events, newest first, and the last hours'.
  rain: (mac, signal, { days = 7 } = {}) => {
    const d = Math.round(Number(days));
    return requestJson(
      `${device(mac, "rain")}?days=${Number.isFinite(d) ? Math.max(1, Math.min(31, d)) : 7}`,
      { signal },
    );
  },
  // Learn or save the profile (POST radar/profile/). The server takes it
  // only with confirm: true, which the page sends once a person has
  // confirmed what it replaces; the profile replaced is kept.
  learnRadarProfile: (mac, body) =>
    requestJson(device(mac, "radar/profile"), json("POST", body)),
  // Put the profile back as it was before its last change (POST
  // radar/profile/undo/ {confirm: true}, again one more change back):
  // {profile, restored: {replaced_at, profile_updated_at, clutter_n}}; 409
  // when there is nothing to go back to.
  undoRadarProfile: (mac) =>
    requestJson(
      device(mac, "radar/profile/undo"),
      json("POST", { confirm: true }),
    ),
  projection: (mac, settings, signal) =>
    requestJson(device(mac, "projection"), {
      ...json("POST", settings),
      signal,
    }),
  ota: (mac, signal) => requestJson(device(mac, "ota"), { signal }),
  uploadRelease: (body) =>
    requestJson(`${base}ota/releases/`, { method: "POST", body }),
  installRelease: (mac, releaseId) =>
    requestJson(
      device(mac, "ota"),
      json("POST", { release_id: releaseId, approve: true }),
    ),
  storage: (signal) => requestJson(`${base}storage/`, { signal }),
  image: (path, signal) => {
    // Only authenticated local frame endpoints may receive the session token.
    if (
      typeof path !== "string" ||
      !/^\/api\/padihat\/rtct\/frames\/[0-9]+\/(file|thumbnail)\/$/.test(path)
    )
      return Promise.reject(new Error("Invalid frame path"));
    return requestBlob(path, { signal });
  },
};
